import { useQuery } from '@tanstack/react-query'
import { AddressZero } from 'constants/index'
import { QUERY_RETRIES } from 'constants/queryClient'
import { useCollection } from 'hooks/useCollectionRegistry'
import { useCallback, useMemo } from 'react'
import { useAddErrorPopup } from 'state/application/hooks'
import { Chain, CollectionType, ItemDetail } from 'types/nft'
import { getCollectionToken, isDataApiNotFound } from 'utils/avalancheDataApi'
import { convertChainToChainId } from 'utils/chain'
import { parseTokenIdQuery } from 'utils/collectionItems'
import { withOnChainOwner } from 'utils/item'
import { toItem } from 'utils/nftItems'
import { getAddress, isAddress } from 'viem'
import { erc721ABI, useAccount, useContractRead } from 'wagmi'

const ERC1155_BALANCE_ABI = [
  {
    inputs: [
      { name: 'account', type: 'address' },
      { name: 'id', type: 'uint256' }
    ],
    name: 'balanceOf',
    outputs: [{ type: 'uint256' }],
    stateMutability: 'view',
    type: 'function'
  }
] as const

export const itemQueryKey = (
  chain: Chain,
  collectionAddress: string | undefined,
  tokenId: string | undefined
): unknown[] => [
  'item',
  convertChainToChainId(chain),
  collectionAddress?.toLowerCase(),
  tokenId
]

interface ItemResult {
  data: ItemDetail | undefined
  isError: boolean
  isFetching: boolean
  isInitialLoading: boolean
}

/**
 * One token from the Data API with the collection fields of the registry.
 * The owner is the indexed one. `silent` skips the error popup and retries.
 * The query key holds the canonical token id, the one the API returns.
 */
export const useCollectionTokenItem = (
  collectionAddress: string | undefined,
  tokenId: string | undefined,
  chain: Chain,
  { silent = false }: { silent?: boolean } = {}
): ItemResult => {
  const chainId = convertChainToChainId(chain)
  const addErrorPopup = useAddErrorPopup()
  const canonicalTokenId =
    tokenId === undefined ? undefined : parseTokenIdQuery(tokenId) ?? tokenId
  const isEnabled =
    !!collectionAddress && isAddress(collectionAddress) && !!canonicalTokenId
  const { data: collection } = useCollection(
    isEnabled ? collectionAddress : '',
    chain
  )

  const onError = useCallback(
    (error: unknown) => {
      if (!silent && error instanceof Error) {
        addErrorPopup(error.message)
      }
    },
    [addErrorPopup, silent]
  )

  const result = useQuery(
    itemQueryKey(chain, collectionAddress, canonicalTokenId),
    () =>
      collectionAddress && canonicalTokenId
        ? getCollectionToken(chainId, collectionAddress, canonicalTokenId)
        : Promise.reject(new Error('Token is not set')),
    {
      enabled: isEnabled,
      onError,
      retry: (failureCount, error) =>
        !silent && !isDataApiNotFound(error) && failureCount < QUERY_RETRIES
    }
  )

  const data = useMemo(
    () => (result.data ? toItem(result.data, chainId, collection) : undefined),
    [chainId, collection, result.data]
  )

  return {
    data,
    isError: result.isError,
    isFetching: result.isFetching,
    isInitialLoading: result.isInitialLoading
  }
}

/**
 * The item page token: ERC-721 owner from on-chain `ownerOf` (the indexed
 * owner when the call reverts), ERC-1155 quantity from `balanceOf` of the
 * connected account.
 */
export const useItem = (
  collectionAddress: string,
  tokenId: string,
  chain: Chain
): ItemResult => {
  const result = useCollectionTokenItem(collectionAddress, tokenId, chain)
  const { address: account } = useAccount()
  const item = result.data

  const canonicalTokenId = parseTokenIdQuery(tokenId)
  const tokenIdArg =
    canonicalTokenId === null ? undefined : BigInt(canonicalTokenId)
  const contract = {
    address: isAddress(collectionAddress)
      ? getAddress(collectionAddress)
      : AddressZero,
    chainId: convertChainToChainId(chain)
  } as const

  const { data: ownerOf } = useContractRead({
    ...contract,
    abi: erc721ABI,
    args: tokenIdArg === undefined ? undefined : [tokenIdArg],
    enabled:
      item?.collectionType === CollectionType.ERC721 &&
      tokenIdArg !== undefined,
    functionName: 'ownerOf'
  })
  const { data: balance } = useContractRead({
    ...contract,
    abi: ERC1155_BALANCE_ABI,
    args:
      account && tokenIdArg !== undefined ? [account, tokenIdArg] : undefined,
    enabled:
      item?.collectionType === CollectionType.ERC1155 &&
      !!account &&
      tokenIdArg !== undefined,
    functionName: 'balanceOf'
  })

  const data = useMemo(
    () =>
      item ? withOnChainOwner(item, { account, balance, ownerOf }) : undefined,
    [account, balance, item, ownerOf]
  )

  return { ...result, data }
}
